import React, { useCallback, useState } from 'react';
import { Icons } from '../../../components/atoms/SVG';
import { ButtonMinorAction } from '../../../components/molecules/Btn/ButtonWrapper';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { AcceptDeclineForm } from '../../../_lib/Form2';
import { useDelayed } from '../../../hooks/useDelayed';
import { PageTypeIcon } from '../../../_lib/components/PageType';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useInvalidateRefreshableBlock, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { PrivilegePageType } from '../PrivilegeConfig.types';
import { BlockType } from '../../../types/api/ElementType';
import { Hint } from '../../../_lib/components/Hint';
import { translatePageType } from '../../../_lib/components/PageType/PageTypeTitle';
import { PageType } from '../../../types/api/PageType';
import { useAppContext } from '../../../contexts/AppContext/AppContext';

type BasicPrivilegeTicketProps = {
  privilegePageType: PrivilegePageType;
};

export function BasicPrivilegeTicket({ privilegePageType }: BasicPrivilegeTicketProps) {
  const { t } = useSonTranslation('PrivilegeConfig');
  const { active } = useAppContext().editModeInfo;

  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();

  const [showModal, setShowModal] = useState(false);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const { refresh } = useRefreshBlock();

  const refreshAndClose = useCallback(() => {
    refresh();
    invalidateRefreshableBlock(BlockType.additionalprivilegeconfig);
    closeModal();
  }, [closeModal, refresh, invalidateRefreshableBlock]);
  const delayedRefreshAndClose = useDelayed(refreshAndClose);

  const pageTypeTitle = translatePageType(privilegePageType.pageType as PageType);
  const deletePageTypeTitle = t('deletePageType', { name: pageTypeTitle });

  return (
    <>
      <div className="privilege-config__basic-privilege-ticket font-bold-13-19">
        <div className="privilege-config__basic-privilege-ticket-label">
          <PageTypeIcon pageType={privilegePageType.pageType} />
          <p>{pageTypeTitle}</p>
        </div>
        {active && privilegePageType.actions?.deletePageType ? (
          <div className="privilege-config__privilege-ticket-button">
            <ButtonMinorAction
              icon={Icons.DELETE_X_SMALL}
              text={deletePageTypeTitle}
              onClick={() => {
                setShowModal(true);
              }}
              tooltipPlacement={TooltipPlacement.TOP}
            />
          </div>
        ) : null}
      </div>
      {showModal && (
        <ModalDialog closeModal={closeModal} headline={deletePageTypeTitle} initialFocusIndex={1}>
          <AcceptDeclineForm
            headline={t('deletePageTypeQuestion')}
            form={privilegePageType.actions.deletePageType}
            messageSuccess={t('deleteStatus.success')}
            messageSending={t('deleteStatus.sending')}
            messageFail={t('deleteStatus.fail')}
            onSuccess={delayedRefreshAndClose}
          >
            <Hint message={t('deleteFormHint')} />
          </AcceptDeclineForm>
        </ModalDialog>
      )}
    </>
  );
}
